/**
 * A hands-free, continuous voice conversation with an agent.
 *
 *   idle → requesting_permission → connecting → listening ⇄ thinking ⇄ speaking
 *   any active state → reconnecting | error | ending → ended
 *
 * One user gesture (Start) asks for the microphone and opens one WebSocket.
 * From then on the mic streams 20 ms frames continuously; the server decides
 * when a turn is over. Agent audio streams back and plays as it arrives. If the
 * server says "clear" (the person talked over the agent), playback is flushed
 * immediately and the time it took is measured and reported.
 *
 * Every exit path (end, error, unmount, page hide) goes through `release()`,
 * which stops the mic tracks, closes the audio graph and the socket.
 */

export type LiveState =
  | 'idle'
  | 'requesting_permission'
  | 'connecting'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'reconnecting'
  | 'error'
  | 'ending'
  | 'ended';

export type LiveErrorCode =
  | 'permission_denied'
  | 'no_device'
  | 'unsupported'
  | 'network'
  | 'server'
  | 'provider_auth'
  | 'provider_timeout'
  | 'provider_unavailable'
  | 'limit'
  | 'agent_unavailable';

export interface TranscriptEntry {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  interrupted: boolean;
}

export interface Diagnostics {
  connectMs: number | null;
  firstPartialMs: number | null;
  turnFinalMs: number | null;
  firstTokenMs: number | null;
  firstAudioMs: number | null;
  firstAudioHeardMs: number | null;
  bargeInDetectMs: number | null;
  bargeInStopMs: number | null;
  turns: number;
  bargeIns: number;
  reconnects: number;
  log: { t: number; name: string; ms: number | null }[];
}

export interface LiveSnapshot {
  state: LiveState;
  conversationId: string | null;
  partial: string;
  agentSaying: string;
  transcript: TranscriptEntry[];
  error: { code: LiveErrorCode; message: string } | null;
  notice: string | null;
  micLevel: number;
  outLevel: number;
  startedAt: number | null;
  diag: Diagnostics;
}

const emptyDiag = (): Diagnostics => ({
  connectMs: null, firstPartialMs: null, turnFinalMs: null, firstTokenMs: null, firstAudioMs: null, firstAudioHeardMs: null,
  bargeInDetectMs: null, bargeInStopMs: null, turns: 0, bargeIns: 0, reconnects: 0, log: [],
});

const ACTIVE: LiveState[] = ['requesting_permission', 'connecting', 'listening', 'thinking', 'speaking', 'reconnecting'];
const BACKOFF_MS = [500, 1000, 2000, 4000, 8000];

export class LiveSession {
  private snap: LiveSnapshot = {
    state: 'idle', conversationId: null, partial: '', agentSaying: '', transcript: [], error: null, notice: null,
    micLevel: 0, outLevel: 0, startedAt: null, diag: emptyDiag(),
  };
  private listeners = new Set<(s: LiveSnapshot) => void>();
  private ws: WebSocket | null = null;
  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private mic: AudioWorkletNode | null = null;
  private player: AudioWorkletNode | null = null;
  private agentId = '';
  private clearedGen = 0;
  private attempt = 0;
  private reconnectTimer: number | null = null;
  private startClickedAt = 0;

  /** `/ws/talk` for a signed-in account; `/ws/link?t=…` for a demo link. */
  constructor(private socketPath = '/ws/talk') {}
  private turnFinalAt = 0;
  private awaitingFirstAudio = false;
  private nextId = 1;
  /** Bumped by dispose(); a start() that was awaiting the mic sees it and backs out. */
  private epoch = 0;
  private emitQueued = false;

  get state(): LiveState {
    return this.snap.state;
  }
  get isActive(): boolean {
    return ACTIVE.includes(this.snap.state);
  }

  subscribe(fn: (s: LiveSnapshot) => void): () => void {
    this.listeners.add(fn);
    fn(this.snap);
    return () => this.listeners.delete(fn);
  }

  private set(patch: Partial<LiveSnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    // Coalesce bursts (levels arrive ~20×/s from two sources) into one render.
    if (this.emitQueued) return;
    this.emitQueued = true;
    queueMicrotask(() => {
      this.emitQueued = false;
      for (const l of this.listeners) l(this.snap);
    });
  }

  private diag(patch: Partial<Diagnostics>, name?: string, ms?: number | null): void {
    const d = { ...this.snap.diag, ...patch };
    if (name) d.log = [...d.log.slice(-40), { t: Date.now(), name, ms: ms ?? null }];
    this.set({ diag: d });
  }

  /* ── Start ─────────────────────────────────────────────────────────────── */

  async start(agentId: string): Promise<void> {
    if (this.isActive) return;
    const epoch = this.epoch;
    this.agentId = agentId;
    this.startClickedAt = performance.now();
    this.attempt = 0;
    this.snap = { ...this.snap, transcript: [], partial: '', agentSaying: '', error: null, notice: null, conversationId: null, diag: emptyDiag(), startedAt: null };
    this.set({ state: 'requesting_permission' });

    if (!navigator.mediaDevices?.getUserMedia || typeof AudioWorkletNode === 'undefined') {
      return this.fail('unsupported', 'This browser cannot stream microphone audio. Try a recent Chrome, Edge, Safari or Firefox.');
    }
    if (!window.isSecureContext) {
      return this.fail('unsupported', 'The microphone needs a secure (https) page.');
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
    } catch (err) {
      const name = (err as DOMException)?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        return this.fail('permission_denied', 'Microphone access was blocked. Allow the microphone for this site in your browser settings, then start again.');
      }
      if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        return this.fail('no_device', 'No microphone was found. Plug one in or check your system sound settings.');
      }
      if (name === 'NotReadableError') {
        return this.fail('no_device', 'Your microphone is being used by another app. Close it and try again.');
      }
      return this.fail('unsupported', 'Could not open the microphone.');
    }
    if (epoch !== this.epoch) return this.release();

    this.set({ state: 'connecting' });
    try {
      this.ctx = new AudioContext({ latencyHint: 'interactive' });
      await this.ctx.audioWorklet.addModule('/worklets/mic.js');
      await this.ctx.audioWorklet.addModule('/worklets/player.js');
      if (this.ctx.state === 'suspended') await this.ctx.resume();
    } catch {
      return this.fail('unsupported', 'Could not start audio processing in this browser.');
    }
    const source = this.ctx.createMediaStreamSource(this.stream);
    this.mic = new AudioWorkletNode(this.ctx, 'mic-processor', { numberOfInputs: 1, numberOfOutputs: 0 });
    this.player = new AudioWorkletNode(this.ctx, 'player-processor', { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [1] });
    source.connect(this.mic);
    this.player.connect(this.ctx.destination);
    this.mic.port.onmessage = (e) => this.onMic(e.data);
    this.player.port.onmessage = (e) => this.onPlayer(e.data);
    // A track can end on its own (headset unplugged, permission revoked).
    for (const track of this.stream.getAudioTracks()) {
      track.onended = () => this.isActive && this.fail('no_device', 'The microphone was disconnected.');
    }
    this.connect();
  }

  /* ── Socket ────────────────────────────────────────────────────────────── */

  private connect(): void {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}${this.socketPath}`);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    let opened = false;

    ws.onopen = () => {
      opened = true;
      ws.send(JSON.stringify({ type: 'start', agentId: this.agentId, resume: this.snap.conversationId ?? undefined }));
    };
    ws.onmessage = (e) => {
      if (typeof e.data === 'string') this.onEvent(JSON.parse(e.data));
      else this.onAudio(e.data as ArrayBuffer);
    };
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (!this.isActive) return;
      if (e.code === 4000 || e.code === 1008) return; // the server already sent a fatal error
      if (e.code === 401 || (!opened && this.attempt === 0 && this.snap.state === 'connecting' && !this.snap.conversationId)) {
        // Never got in: most likely signed out or the server is down.
        return this.scheduleReconnect();
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (!this.isActive) return;
    if (this.attempt >= BACKOFF_MS.length) {
      return this.fail('network', 'Lost the connection to the voice server and could not get it back. Check your internet and start again.');
    }
    const delay = BACKOFF_MS[this.attempt++];
    this.flushPlayback();
    this.set({ state: 'reconnecting', notice: `Connection lost. Reconnecting in ${Math.round(delay / 100) / 10}s…`, partial: '', agentSaying: '' });
    this.diag({ reconnects: this.snap.diag.reconnects + 1 }, 'reconnect');
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.isActive) return;
      if (!navigator.onLine) return this.scheduleReconnect();
      this.connect();
    }, delay);
  }

  private onEvent(m: any): void {
    switch (m.type) {
      case 'ready': {
        this.attempt = 0;
        const first = this.snap.diag.connectMs === null;
        this.set({ conversationId: m.conversationId, notice: m.resumed ? 'Reconnected. The conversation continues.' : null, startedAt: this.snap.startedAt ?? Date.now() });
        if (first) this.diag({ connectMs: Math.round(performance.now() - this.startClickedAt) }, 'connect', Math.round(performance.now() - this.startClickedAt));
        break;
      }
      case 'state':
        if (m.state === 'ended') break;
        if (this.isActive) this.set({ state: m.state, ...(m.state === 'listening' ? { agentSaying: '' } : {}) });
        if (m.state === 'listening' && this.snap.notice?.startsWith('Reconnected')) this.set({ notice: null });
        break;
      case 'partial':
        this.set({ partial: m.text });
        break;
      case 'assistant_progress':
        this.set({ agentSaying: m.text });
        break;
      case 'transcript': {
        const entry: TranscriptEntry = { id: this.nextId++, role: m.role, text: m.text, interrupted: Boolean(m.interrupted) };
        let list = this.snap.transcript;
        if (m.replaces) {
          const idx = list.map((t) => t.role).lastIndexOf('assistant');
          list = idx >= 0 ? list.filter((_, i) => i !== idx) : list;
          if (!m.text) {
            this.set({ transcript: list });
            break;
          }
        }
        this.set({ transcript: [...list, entry], ...(m.role === 'user' ? { partial: '' } : {}) });
        break;
      }
      case 'clear': {
        this.clearedGen = Math.max(this.clearedGen, m.gen);
        this.flushPlayback(performance.now());
        this.set({ agentSaying: '' });
        break;
      }
      case 'barge_in':
        this.diag({ bargeIns: this.snap.diag.bargeIns + 1, bargeInDetectMs: m.detectMs || null }, 'barge_in_detect', m.detectMs || null);
        break;
      case 'metric':
        this.onMetric(m);
        break;
      case 'notice':
        this.set({ notice: m.message });
        break;
      case 'error':
        this.onServerError(m);
        break;
      case 'ended':
        this.set({ state: this.snap.state === 'error' ? 'error' : 'ended', notice: endNotice(m.reason) });
        this.release();
        break;
    }
  }

  private onMetric(m: { name: string; ms: number | null; firstPartialMs?: number | null }): void {
    switch (m.name) {
      case 'turn_final':
        this.turnFinalAt = performance.now();
        this.awaitingFirstAudio = true;
        this.diag({ turns: this.snap.diag.turns + 1, turnFinalMs: m.ms, firstPartialMs: m.firstPartialMs ?? this.snap.diag.firstPartialMs }, 'turn_final', m.ms);
        break;
      case 'first_token':
        this.diag({ firstTokenMs: m.ms }, 'first_token', m.ms);
        break;
      case 'first_audio':
        this.diag({ firstAudioMs: m.ms }, 'first_audio_server', m.ms);
        break;
      case 'barge_in_stop':
        break;
    }
  }

  private onServerError(m: { code: string; message: string; fatal?: boolean }): void {
    const map: Record<string, LiveErrorCode> = {
      provider_auth: 'provider_auth',
      provider_not_configured: 'provider_unavailable',
      provider_rate_limited: 'provider_unavailable',
      provider_upstream: 'provider_unavailable',
      provider_network: 'provider_unavailable',
      provider_timeout: 'provider_timeout',
      too_many_sessions: 'limit',
      agent_unavailable: 'agent_unavailable',
    };
    const code = map[m.code] ?? 'server';
    if (m.fatal || code === 'provider_auth' || code === 'limit' || code === 'agent_unavailable') {
      this.fail(code, friendly(code, m.message));
    } else {
      // A hiccup on one turn (a slow brain, a TTS blip): say so, keep listening.
      this.set({ notice: friendly(code, m.message) });
    }
  }

  /* ── Audio ─────────────────────────────────────────────────────────────── */

  private onMic(m: { type: string; pcm?: ArrayBuffer; rms?: number }): void {
    if (m.type === 'frame' && m.pcm) {
      if (this.ws?.readyState === WebSocket.OPEN && this.snap.conversationId) this.ws.send(m.pcm);
    } else if (m.type === 'level') {
      this.set({ micLevel: Math.min(1, (m.rms ?? 0) * 6) });
    }
  }

  private onAudio(buf: ArrayBuffer): void {
    if (buf.byteLength <= 4) return;
    const gen = new DataView(buf).getUint32(0, true);
    if (gen <= this.clearedGen) return; // stale audio from a reply that was interrupted
    this.player?.port.postMessage({ type: 'audio', pcm: buf.slice(4), rate: 24000 }, []);
  }

  private onPlayer(m: { type: string; rms?: number; t?: number }): void {
    if (m.type === 'level') this.set({ outLevel: Math.min(1, (m.rms ?? 0) * 5) });
    else if (m.type === 'playing' && this.awaitingFirstAudio) {
      this.awaitingFirstAudio = false;
      const ms = Math.round(performance.now() - this.turnFinalAt);
      this.diag({ firstAudioHeardMs: ms }, 'first_audio_heard', ms);
    } else if (m.type === 'cleared' && typeof m.t === 'number' && m.t > 0) {
      const outputLatency = ((this.ctx as any)?.outputLatency ?? 0) * 1000;
      const ms = Math.round(performance.now() - m.t + outputLatency);
      this.diag({ bargeInStopMs: ms }, 'barge_in_stop', ms);
      if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'cleared', stopMs: ms }));
    }
  }

  private flushPlayback(t = 0): void {
    this.player?.port.postMessage({ type: 'clear', t });
  }

  /* ── Text fallback, end, cleanup ───────────────────────────────────────── */

  sendText(text: string): void {
    const clean = text.trim();
    if (!clean || this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ type: 'text', text: clean }));
  }

  end(): void {
    if (!this.isActive) return;
    this.set({ state: 'ending' });
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'end' }));
      // If the server does not confirm promptly, release anyway.
      window.setTimeout(() => this.snap.state === 'ending' && (this.set({ state: 'ended' }), this.release()), 1500);
    } else {
      this.set({ state: 'ended' });
      this.release();
    }
  }

  private fail(code: LiveErrorCode, message: string): void {
    this.set({ state: 'error', error: { code, message }, notice: null });
    this.release();
  }

  /** Stops the mic, closes audio and the socket. Idempotent. */
  release(): void {
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    for (const t of this.stream?.getTracks() ?? []) {
      t.onended = null;
      t.stop();
    }
    this.stream = null;
    try {
      this.mic?.disconnect();
      this.player?.disconnect();
    } catch {
      /* already disconnected */
    }
    if (this.mic) this.mic.port.onmessage = null;
    if (this.player) this.player.port.onmessage = null;
    this.mic = null;
    this.player = null;
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    const ws = this.ws;
    this.ws = null;
    if (ws && ws.readyState <= WebSocket.OPEN) ws.close(1000, 'client_release');
    this.set({ micLevel: 0, outLevel: 0, partial: '', agentSaying: '' });
  }

  reset(): void {
    if (this.isActive) return;
    this.set({ state: 'idle', error: null, notice: null });
  }

  /** For component unmount / page hide: end politely if possible, always release. */
  dispose(): void {
    this.epoch++;
    if (this.ws?.readyState === WebSocket.OPEN && this.isActive) this.ws.send(JSON.stringify({ type: 'end' }));
    this.release();
    if (this.isActive) this.set({ state: 'idle' });
  }

  /** Exposed for automated checks: whether any mic track is still live. */
  get micLive(): boolean {
    return Boolean(this.stream?.getAudioTracks().some((t) => t.readyState === 'live'));
  }
}

/**
 * The server decides the words (server/src/problems.ts): the provider's own
 * message for the owner or a teammate, a plain sentence for anyone else. These
 * are only for an event that arrives with none.
 */
function friendly(code: LiveErrorCode, fallback: string): string {
  if (fallback) return fallback;
  switch (code) {
    case 'provider_auth':
      return 'Sorry, the agent cannot talk right now. Please try again in a little while.';
    case 'provider_timeout':
      return 'The agent took too long to answer that time. Say it again.';
    default:
      return 'Sorry, the agent could not answer that. Please say it again.';
  }
}

function endNotice(reason: string): string | null {
  switch (reason) {
    case 'user_ended':
      return 'Conversation ended.';
    case 'agent_ended':
      return 'The agent ended the conversation.';
    case 'time_limit':
      return 'This demo session reached its time limit.';
    default:
      return reason?.startsWith('error') ? null : 'Conversation ended.';
  }
}
